import { Sparkles, Phone, MessageSquare, CheckCircle2, MapPin, Search } from 'lucide-react';
import { SectionPageEditor } from '@/components/admin/SectionPageEditor';
import type { SectionDef } from '@/components/admin/SectionPageEditor';

// pages/contact/page.tsx is a single file, no dead files. The inquiry
// FORM's underlying wiring (field `name` attributes, the third-party
// submit endpoint, validation) is deliberately excluded — same rationale
// as Login/Checkout elsewhere — only its labels/placeholders/options/
// button text are editable below. The two Google Maps embeds are
// hardcoded iframe URLs, not editable here. No useSiteImage anywhere on
// this page.
const CONTACT_SECTIONS: SectionDef[] = [
  {
    key: 'hero',
    title: 'Hero',
    icon: Sparkles,
    texts: [
      { key: 'contact.hero.label', label: 'Eyebrow Label' },
      { key: 'contact.hero.heading', label: 'Heading' },
      { key: 'contact.hero.subtitle', label: 'Subtitle' },
    ],
  },
  {
    key: 'channels',
    title: 'Contact Channels',
    icon: Phone,
    texts: [
      { key: 'contact.channels.email.label', label: 'Email — Label' },
      { key: 'contact.channels.email.value', label: 'Email — Value' },
      { key: 'contact.channels.email.note', label: 'Email — Note' },
      { key: 'contact.channels.whatsapp.label', label: 'WhatsApp — Label' },
      { key: 'contact.channels.whatsapp.value', label: 'WhatsApp — Value' },
      { key: 'contact.channels.whatsapp.note', label: 'WhatsApp — Note' },
      { key: 'contact.channels.hq.label', label: 'Korea HQ — Label' },
      { key: 'contact.channels.hq.value', label: 'Korea HQ — Value' },
      { key: 'contact.channels.hq.note', label: 'Korea HQ — Note' },
    ],
  },
  {
    key: 'form',
    title: 'Send a Message (Form Text Only)',
    icon: MessageSquare,
    note: 'The form itself (field names, submit endpoint, validation) is fixed — only the visible text below is editable here.',
    texts: [
      { key: 'contact.sendMessage.heading', label: 'Section Heading' },
      { key: 'contact.form.name', label: 'Field — Name Label' },
      { key: 'contact.form.namePlaceholder', label: 'Field — Name Placeholder' },
      { key: 'contact.form.email', label: 'Field — Email Label' },
      { key: 'contact.form.emailPlaceholder', label: 'Field — Email Placeholder' },
      { key: 'contact.form.phone', label: 'Field — Phone Label' },
      { key: 'contact.form.phoneHelper', label: 'Field — Phone Helper Text' },
      { key: 'contact.form.subject', label: 'Field — Subject Label' },
      { key: 'contact.form.subjectPlaceholder', label: 'Field — Subject Placeholder' },
      { key: 'contact.form.subjectProductInquiry', label: 'Subject Option — Product Inquiry' },
      { key: 'contact.form.subjectPharmacyPartnership', label: 'Subject Option — Pharmacy Partnership' },
      { key: 'contact.form.subjectInfluencer', label: 'Subject Option — Influencer' },
      { key: 'contact.form.subjectMediK', label: 'Subject Option — Medi.K Seoul' },
      { key: 'contact.form.subjectPickK', label: 'Subject Option — Pick.K Seoul' },
      { key: 'contact.form.subjectCounterfeit', label: 'Subject Option — Counterfeit Report' },
      { key: 'contact.form.subjectOther', label: 'Subject Option — Other' },
      { key: 'contact.form.message', label: 'Field — Message Label' },
      { key: 'contact.form.messagePlaceholder', label: 'Field — Message Placeholder' },
      { key: 'contact.form.maxChars', label: 'Character Limit Note' },
      { key: 'contact.form.messageMinError', label: 'Message Too Short Error' },
      { key: 'contact.form.consent', label: 'Consent Checkbox Text', long: true },
      { key: 'contact.form.submit', label: 'Submit Button' },
      { key: 'contact.form.sending', label: 'Submitting Button Text' },
      { key: 'contact.form.sendAnother', label: 'Send Another Button' },
    ],
  },
  {
    key: 'formSuccess',
    title: 'Form Success State',
    icon: CheckCircle2,
    texts: [
      { key: 'contact.success.heading', label: 'Heading' },
      { key: 'contact.success.body', label: 'Body Text', long: true },
      { key: 'contact.success.toast', label: 'Toast Notification' },
    ],
  },
  {
    key: 'offices',
    title: 'Office Locations',
    icon: MapPin,
    note: 'The embedded Google Maps locations are fixed in code — only the text below is editable here.',
    texts: [
      { key: 'contact.offices.bd.title', label: 'Bangladesh — Title' },
      { key: 'contact.offices.bd.company', label: 'Bangladesh — Company' },
      { key: 'contact.offices.bd.address', label: 'Bangladesh — Address' },
      { key: 'contact.offices.korea.title', label: 'Korea HQ — Title' },
      { key: 'contact.offices.korea.company', label: 'Korea HQ — Company' },
      { key: 'contact.offices.korea.address', label: 'Korea HQ — Address' },
    ],
  },
  {
    key: 'seo',
    title: 'SEO',
    icon: Search,
    note: 'Shown in Google search results and social media link previews — not visible on the page itself.',
    texts: [
      { key: 'seo.contact.title', label: 'Meta Title', long: true },
      { key: 'seo.contact.description', label: 'Meta Description', long: true },
    ],
  },
];

export default function AdminContactPage() {
  return <SectionPageEditor pageKey="contact" title="Contact" sections={CONTACT_SECTIONS} />;
}
